import { z } from '@vigilancia/runtime';

/**
 * LO QUE LAS NOTIFICACIONES LE PIDEN A LA API · 🆕 5/10/2026, segunda vuelta (ADR-0021).
 *
 * Vive en el cascarón porque la campana y la página son suyas: las montan las tres
 * aplicaciones, y una sola copia de cómo se pide y cómo se lee no diverge.
 *
 * ⚠️ **SE VALIDA LO QUE LLEGA**: un 200 no dice quién lo devolvió. Una notificación
 * con otra forma es un fallo de integración, no una lista vacía. **Salvo el tipo que
 * esta versión no conoce**, que se deja fuera: un tipo nuevo puede llegar a la base
 * antes que a la aplicación desplegada.
 *
 * ⚠️ **EL IDENTIFICADOR SE VALIDA POR SU FORMA, NO COMO UUID**: el de la respuesta a
 * una petición es un `md5` vuelto `uuid`, sin versión ni variante, y `z.uuid()` lo
 * rechaza.
 *
 * ⚠️ **NADA DE ESTO SE REGISTRA**: una notificación lleva nombres de personas y de
 * vistas.
 */

/** Lo único que justifica esperar es el arranque en frío de la API: 1,9 a 3,7 s medidos. */
const ESPERA_MAXIMA_MS = 15_000;

/** Cuáles: las activas —por omisión—, las sin leer, las por atender o el historial. */
export const VISTAS_DE_NOTIFICACIONES = [
  'activas',
  'sin-leer',
  'por-atender',
  'historial',
] as const;
export type VistaDeNotificaciones = (typeof VISTAS_DE_NOTIFICACIONES)[number];

/** Lo que se hace de golpe: todas leídas, o archivar las leídas. */
export type AccionDeGolpe = 'leidas' | 'archivar-leidas';

/** Cuántas se marcan como mucho de una vez: lo que admite la API. */
export const MAXIMO_A_MARCAR = 200;

/** Cuántas trae una página como mucho: lo que admite la API. */
export const MAXIMO_POR_PAGINA = 100;

const IDENTIFICADOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const ID = z
  .string()
  .regex(IDENTIFICADOR)
  .transform((id) => id.toLowerCase());
const INSTANTE = z.iso.datetime({ offset: true });

const PERSONA = z.object({ persona: z.int().min(1), nombre: z.string().nullable() });
export type PersonaEnLaNotificacion = z.infer<typeof PERSONA>;

const OBJETO = z.object({
  clase: z.string().min(1),
  id: z.string().min(1),
  nombre: z.string().nullable(),
  en: z.object({ clase: z.string().min(1), id: z.string().min(1) }).nullable(),
});
export type ObjetoDeLaNotificacion = z.infer<typeof OBJETO>;

/** El sobre: lo que tienen todas, sea cual sea su tipo. */
const SOBRE = z.object({
  id: ID,
  tipo: z.string().regex(/^[a-z0-9-]{1,60}$/u),
  momento: INSTANTE,
  leida: z.boolean(),
  archivada: z.boolean(),
  porAtender: z.boolean(),
  estado: z.string().nullable(),
  actor: PERSONA.nullable(),
  objeto: OBJETO.nullable(),
  para: PERSONA.nullable(),
  datos: z.record(z.string(), z.unknown()),
});
type Sobre = z.infer<typeof SOBRE>;

/** Lo propio de cada tipo que esta versión conoce. */
const DATOS = {
  'vista-compartida': z.object({
    permiso: z.enum(['ver', 'editar']),
    rol: z.object({ id: z.string().min(1), nombre: z.string().nullable() }).nullable(),
  }),
  'acceso-pedido': z.object({
    falta: z.array(z.enum(['modulo', 'pagina', 'reporte'])).max(3),
    loDan: z.array(z.string()),
    // 🆕 5/10/2026 · Sin quién, la que se dio sola: se la dieron en el HIS (D18).
    resuelta: z.object({ por: PERSONA.nullable(), en: INSTANTE }).nullable(),
  }),
  'acceso-dado': z.object({}),
  'acceso-descartado': z.object({}),
} as const;

export type TipoConocido = keyof typeof DATOS;
export const TIPOS_CONOCIDOS = Object.keys(DATOS) as readonly TipoConocido[];

type Comun = Omit<Sobre, 'tipo' | 'datos'>;
export type Notificacion = {
  [T in TipoConocido]: Comun & { readonly tipo: T; readonly datos: z.infer<(typeof DATOS)[T]> };
}[TipoConocido];

export type NivelQueFalta = z.infer<(typeof DATOS)['acceso-pedido']>['falta'][number];

const esConocido = (tipo: string): tipo is TipoConocido => Object.hasOwn(DATOS, tipo);

const PAGINA = z.object({
  notificaciones: z.array(z.unknown()).max(100),
  siguiente: z.string().min(1).max(200).nullable(),
});

const CUENTA = z.object({
  sinLeer: z.int().min(0),
  activas: z.int().min(0),
  porAtender: z.int().min(0),
});
export type Cuenta = z.infer<typeof CUENTA>;

const CAMBIADAS = z.object({ ids: z.array(ID) });

export interface PaginaDeNotificaciones {
  readonly notificaciones: readonly Notificacion[];
  /** El cursor de la siguiente, tal cual —opaco—; `null` si no hay más. */
  readonly siguiente: string | null;
}

export interface PedirUnaPagina {
  readonly vista?: VistaDeNotificaciones;
  readonly tipos?: readonly TipoConocido[];
  readonly desde?: string | null;
  readonly limite?: number;
}

/** Qué se le hace a unas cuantas: leída o no, archivada o restaurada. */
export interface Cambio {
  readonly leida?: boolean;
  readonly archivada?: boolean;
}

/**
 * Por qué no se pudo. `sin-sesion` es el 401: la sesión caducó, y las notificaciones
 * dejan de preguntar —lo descubrirá la pantalla en su próxima petición—.
 */
export class NotificacionesNoDisponibles extends Error {
  constructor(readonly motivo: 'sin-sesion' | 'fallo') {
    super(motivo === 'sin-sesion' ? 'la sesión caducó' : 'la API no contestó lo esperado');
    this.name = 'NotificacionesNoDisponibles';
  }
}

/** Cómo acabó descartar una petición, dicho para la pantalla. Lo dice el código, nunca el texto. */
export type ResultadoDeDescartar = 'hecho' | 'ya-resuelta' | 'retirada' | 'sin-sesion' | 'fallo';

export interface ServicioDeNotificaciones {
  pagina(pedido?: PedirUnaPagina): Promise<PaginaDeNotificaciones>;
  cuenta(): Promise<Cuenta>;
  /**
   * Cambia unas cuantas y devuelve las que cambiaron: una que ya no está —la
   * retiraron, o su vista se borró— no es un error. `alSalir` la manda con
   * `keepalive`: abrir una notificación navega, y la petición tiene que llegar.
   */
  marcar(
    ids: readonly string[],
    cambio: Cambio,
    opciones?: { readonly alSalir?: boolean },
  ): Promise<readonly string[]>;
  /** Todas leídas, o las leídas archivadas. Devuelve las que cambiaron: para deshacerlo. */
  deGolpe(accion: AccionDeGolpe): Promise<readonly string[]>;
  /**
   * 🆕 5/10/2026 · Descarta una petición. **A mano sólo se descarta** (D18): se da sola
   * cuando el RBAC dice que la persona ya tiene lo que le faltaba.
   */
  descartar(id: string): Promise<ResultadoDeDescartar>;
}

interface PedirOpciones {
  readonly metodo?: 'GET' | 'PUT';
  readonly cuerpo?: unknown;
  readonly keepalive?: boolean;
}

export function servicioDeNotificaciones(
  apiUrl: string,
  buscar: typeof fetch = fetch,
): ServicioDeNotificaciones {
  const base = apiUrl.replace(/\/+$/u, '');

  /**
   * `same-origin` y no `include`: si algún día alguien apunta `apiUrl` a otro
   * origen, la petición irá SIN cookie en vez de mandarla fuera.
   */
  const pedir = async (
    ruta: string,
    { metodo = 'GET', cuerpo, keepalive = false }: PedirOpciones = {},
  ): Promise<Response> => {
    const cabeceras = new Headers({ accept: 'application/json' });
    if (cuerpo !== undefined) cabeceras.set('content-type', 'application/json');
    try {
      return await buscar(`${base}${ruta}`, {
        method: metodo,
        credentials: 'same-origin',
        headers: cabeceras,
        keepalive,
        signal: AbortSignal.timeout(ESPERA_MAXIMA_MS),
        ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
      });
    } catch {
      throw new NotificacionesNoDisponibles('fallo');
    }
  };

  const comprobar = (respuesta: Response): void => {
    if (respuesta.status === 401) throw new NotificacionesNoDisponibles('sin-sesion');
    if (!respuesta.ok) throw new NotificacionesNoDisponibles('fallo');
  };

  const leer = async <T>(respuesta: Response, esquema: z.ZodType<T>): Promise<T> => {
    comprobar(respuesta);
    let crudo: unknown;
    try {
      crudo = await respuesta.json();
    } catch {
      throw new NotificacionesNoDisponibles('fallo');
    }
    const analisis = esquema.safeParse(crudo);
    if (!analisis.success) throw new NotificacionesNoDisponibles('fallo');
    return analisis.data;
  };

  /** Lo que sale se comprueba antes de salir: un fallo de la pantalla se ve aquí, no como un 400. */
  const idsValidos = (ids: readonly string[]): string[] => {
    if (ids.length === 0 || ids.length > MAXIMO_A_MARCAR) {
      throw new NotificacionesNoDisponibles('fallo');
    }
    return ids.map((id) => {
      if (!IDENTIFICADOR.test(id)) throw new NotificacionesNoDisponibles('fallo');
      return id.toLowerCase();
    });
  };

  return {
    async pagina({ vista = 'activas', tipos, desde = null, limite } = {}) {
      const consulta = new URLSearchParams({ vista });
      if (tipos !== undefined && tipos.length > 0) consulta.set('tipos', tipos.join(','));
      if (desde !== null) consulta.set('desde', desde);
      if (limite !== undefined) consulta.set('limite', String(limite));
      const leida = await leer(await pedir(`/notificaciones?${consulta.toString()}`), PAGINA);
      return {
        notificaciones: deLosTiposConocidos(leida.notificaciones),
        siguiente: leida.siguiente,
      };
    },

    async cuenta() {
      return await leer(await pedir('/notificaciones/cuenta'), CUENTA);
    },

    async marcar(ids, cambio, { alSalir = false } = {}) {
      // Archivar es también leer: archivar sin leer no existe, y la API lo rechazaría.
      if (cambio.leida === undefined && cambio.archivada === undefined) {
        throw new NotificacionesNoDisponibles('fallo');
      }
      if (cambio.archivada === true && cambio.leida === false) {
        throw new NotificacionesNoDisponibles('fallo');
      }
      const respuesta = await pedir('/notificaciones/estado', {
        metodo: 'PUT',
        cuerpo: { ids: idsValidos(ids), ...cambio },
        keepalive: alSalir,
      });
      return (await leer(respuesta, CAMBIADAS)).ids;
    },

    async deGolpe(accion) {
      const respuesta = await pedir('/notificaciones/todas', { metodo: 'PUT', cuerpo: { accion } });
      return (await leer(respuesta, CAMBIADAS)).ids;
    },

    async descartar(id) {
      if (!IDENTIFICADOR.test(id)) return 'fallo';
      let respuesta: Response;
      try {
        respuesta = await pedir(`/accesos-pedidos/${id.toLowerCase()}/estado`, {
          metodo: 'PUT',
          cuerpo: { estado: 'descartado' },
        });
      } catch {
        return 'fallo';
      }
      if (respuesta.ok) return 'hecho';
      if (respuesta.status === 401) return 'sin-sesion';
      // Ya atendida: la descartó otra persona, o se dio sola.
      if (respuesta.status === 409) return 'ya-resuelta';
      // Retirada (D18): para todos, ya no existe.
      if (respuesta.status === 404) return 'retirada';
      return 'fallo';
    },
  };
}

/**
 * EL SERVICIO, AVISANDO CUANDO LA SESIÓN CADUCÓ: un 401 en cualquiera de sus llamadas
 * llama a `alCaducar` —y la llamada falla o contesta como siempre—. Así la campana y
 * la página lo dicen una vez y como lo que es: caducar tras ocho horas es lo normal y
 * se arregla volviendo a entrar, no reintentando.
 */
export function vigilarLaSesion(
  servicio: ServicioDeNotificaciones,
  alCaducar: () => void,
): ServicioDeNotificaciones {
  const vigilar = async <T>(llamada: Promise<T>): Promise<T> => {
    try {
      return await llamada;
    } catch (error) {
      if (error instanceof NotificacionesNoDisponibles && error.motivo === 'sin-sesion')
        alCaducar();
      throw error;
    }
  };
  // Cada llamada pasa tal cual, con los argumentos que traiga y ni uno más.
  return {
    pagina: (...argumentos) => vigilar(servicio.pagina(...argumentos)),
    cuenta: () => vigilar(servicio.cuenta()),
    marcar: (...argumentos) => vigilar(servicio.marcar(...argumentos)),
    deGolpe: (...argumentos) => vigilar(servicio.deGolpe(...argumentos)),
    async descartar(...argumentos) {
      const resultado = await servicio.descartar(...argumentos);
      if (resultado === 'sin-sesion') alCaducar();
      return resultado;
    },
  };
}

/**
 * Cada una con su sobre y con lo propio de su tipo. **La de un tipo que esta versión
 * no conoce se deja fuera**; la de uno conocido con otra forma es un fallo.
 */
function deLosTiposConocidos(crudas: readonly unknown[]): Notificacion[] {
  const conocidas: Notificacion[] = [];
  for (const cruda of crudas) {
    const sobre = SOBRE.safeParse(cruda);
    if (!sobre.success) throw new NotificacionesNoDisponibles('fallo');
    const { tipo, datos, ...comun } = sobre.data;
    if (!esConocido(tipo)) continue;
    const propios = DATOS[tipo].safeParse(datos);
    if (!propios.success) throw new NotificacionesNoDisponibles('fallo');
    conocidas.push({ ...comun, tipo, datos: propios.data } as Notificacion);
  }
  return conocidas;
}
